import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  FormLabel,
  Select,
  MenuItem,
  Slider,
  TextField,
  Button,
  Grid,
  Paper,
  CircularProgress,
  useMediaQuery,
  useTheme,
  Stack,
  Divider,
  Tooltip,
} from "@mui/material";
import BASE_URL from "../../config/apiConfig";

const PricingCalculator = () => {
  const [cropType, setCropType] = useState("plantation");
  const [crops, setCrops] = useState([]);
  const [selectedCrop, setSelectedCrop] = useState(null);
  const [landSize, setLandSize] = useState(50);
  const [manualLandSize, setManualLandSize] = useState(50);
  const [paymentFrequency, setPaymentFrequency] = useState(1);
  const [calculatedPrice, setCalculatedPrice] = useState(null);
  const [loading, setLoading] = useState(false);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  useEffect(() => {
    const fetchCrops = async () => {
      setLoading(true);
      try {
        const response = await fetch(
          `${BASE_URL}/api/${cropType === "plantation" ? "plantation-crops" : "field-crops"}`
        );
        if (!response.ok) throw new Error("Failed to fetch crops");

        const data = await response.json();
        const formatted = data.map((crop) => ({
          id: crop.crop_id,
          name: crop.crop_name,
          perHectarePrice: crop.per_hectare_price,
        }));
        setCrops(formatted);
        setSelectedCrop(formatted[0] || null);
      } catch (error) {
        console.error("Error fetching crops:", error);
        setCrops([]);
        setSelectedCrop(null);
      } finally {
        setLoading(false);
        setCalculatedPrice(null);
      }
    };

    fetchCrops();
  }, [cropType]);

  const handleManualLandSizeChange = (e) => {
    let value = parseInt(e.target.value, 10);
    if (isNaN(value)) value = 50;
    value = Math.max(50, Math.round(value / 50) * 50);
    setManualLandSize(value);
    setLandSize(value);
  };

  const handleCalculate = async () => {
    if (!selectedCrop) return;

    try {
      const response = await fetch(`${BASE_URL}/api/calculate-price`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          landSize,
          perHectarePrice: selectedCrop.perHectarePrice,
        }),
      });

      if (!response.ok) throw new Error("Failed to calculate price");

      const data = await response.json();
      let finalPrice = data.calculatedPrice;

      if (paymentFrequency === 3) finalPrice *= 3 * 0.9;
      if (paymentFrequency === 12) finalPrice *= 12 * 0.8;

      setCalculatedPrice(finalPrice);
    } catch (err) {
      console.error("Calculation failed:", err);
    }
  };

  useEffect(() => {
    if (selectedCrop) {
      handleCalculate();
    }
  }, [selectedCrop, landSize, paymentFrequency]);

  const getFrequencyLabel = () =>
    ({
      1: "month",
      3: "quarter",
      12: "year",
    })[paymentFrequency];

  return (
    <Paper
      elevation={3}
      sx={{
        p: { xs: 3, sm: 4 },
        borderRadius: 4,
        backgroundColor: "#f4fdf8",
        maxWidth: 1100,
        mx: "auto",
        mb: 5,
      }}
    >
    

      <Grid container spacing={4}>
        {/* Left Column */}
        <Grid item xs={12} md={5}>
          <Stack spacing={4}>
            <FormControl component="fieldset">
              <FormLabel
                sx={{ fontWeight: "bold", color: "success.main", mb: 1 }}
              >
                Crop Type
              </FormLabel>
              <Box textAlign="center">
                <RadioGroup
                  row
                  value={cropType}
                  onChange={(e) => setCropType(e.target.value)}
                  sx={{ justifyContent: "center", gap: 10, mt: 1 }}
                >
                  <FormControlLabel
                    value="plantation"
                    control={<Radio />}
                    label="Plantation"
                  />
                  <FormControlLabel
                    value="field"
                    control={<Radio />}
                    label="Field Crop"
                  />
                </RadioGroup>
              </Box>
            </FormControl>

            <FormControl fullWidth>
              <FormLabel
                sx={{ fontWeight: "bold", color: "success.main", mt: 2 }}
              >
                Select Crop
              </FormLabel>
              {loading ? (
                <Box textAlign="center" mt={4}>
                  <CircularProgress size={24} />
                </Box>
              ) : (
                <Select
                  fullWidth
                  value={selectedCrop?.name || ""}
                  onChange={(e) =>
                    setSelectedCrop(
                      crops.find((c) => c.name === e.target.value)
                    )
                  }
                  sx={{ mt: 1, backgroundColor: "#fff", borderRadius: 2 }}
                >
                  {crops.length === 0 ? (
                    <MenuItem disabled>No crops available</MenuItem>
                  ) : (
                    crops.map((crop) => (
                      <MenuItem key={crop.id} value={crop.name}>
                        {crop.name}
                      </MenuItem>
                    ))
                  )}
                </Select>
              )}
            </FormControl>
            <FormControl fullWidth>
              <FormLabel sx={{ fontWeight: "bold", color: "success.main" }}>
                Land Size (Hectares)
              </FormLabel>
              <TextField
                type="number"
                fullWidth
                value={manualLandSize}
                onChange={handleManualLandSizeChange}
                inputProps={{ min: 50, step: 50 }}
                sx={{ mt: 1, backgroundColor: "#fff", borderRadius: 2 }}
              />
            </FormControl>

            <Box>
              <Slider
                value={landSize}
                min={50}
                max={50000}
                step={50}
                onChange={(e, val) => {
                  setLandSize(val);
                  setManualLandSize(val);
                }}
                sx={{}}
                valueLabelDisplay="auto"
              />
              <Typography variant="body2" color="text.secondary" mt={0}>
                🌍 Selected: <strong>{landSize}</strong> hectares
              </Typography>
            </Box>
          </Stack>
        </Grid>

        {/* Vertical Divider */}
        {!isMobile && (
          <Grid item md={1} sx={{ display: "flex", justifyContent: "center" }}>
            <Divider
              orientation="vertical"
              flexItem
              sx={{ borderColor: "success.light" }}
            />
          </Grid>
        )}

        {/* Right Column */}
        <Grid item xs={12} md={6}>
          <Stack spacing={4}>
            <FormControl component="fieldset">
              <FormLabel
                sx={{ fontWeight: "bold", color: "success.main", mb: 1 }}
              >
                Payment Frequency
              </FormLabel>
              <Box textAlign="center">
                <RadioGroup
                  row={!isMobile}
                  value={paymentFrequency}
                  onChange={(e) =>
                    setPaymentFrequency(parseInt(e.target.value))
                  }
                  sx={{ justifyContent: "center", gap: 10, mt: 1 }}
                >
                  <FormControlLabel
                    value={1}
                    control={<Radio />}
                    label="Monthly"
                  />

                  <FormControlLabel
                    value={3}
                    control={<Radio />}
                    label={
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "center",
                        }}
                      >
                        <Typography variant="body2">Quarterly</Typography>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ fontStyle: "italic" }}
                        >
                          10% Off
                        </Typography>
                      </Box>
                    }
                  />

                  <FormControlLabel
                    value={12}
                    control={<Radio />}
                    label={
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "center",
                        }}
                      >
                        <Typography variant="body2">Yearly</Typography>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ fontStyle: "italic" }}
                        >
                          20% Off
                        </Typography>
                      </Box>
                    }
                  />
                </RadioGroup>
              </Box>
            </FormControl>

            {/* Hypothetical Data Disclaimer */}
            <Box sx={{ textAlign: "center", mt: 2, color: "text.secondary" }}>
              <Typography variant="body2">
                <strong>⚠️ Please note:</strong> The calculated price is an
                estimate based on the data provided. Actual prices may vary
                depending on specific farm conditions. This calculator is here
                to give you an idea of potential costs.
              </Typography>
            </Box>

            <Button
              fullWidth
              variant="contained"
              onClick={handleCalculate}
              disabled={!selectedCrop || loading}
              sx={{
                backgroundColor: "success.main",
                fontWeight: "bold",
                py: 2,
                borderRadius: 3,
                "&:hover": { backgroundColor: "success.dark" },
              }}
            >
              Calculate Price
            </Button>

            {/* Display Calculated Price */}
            {calculatedPrice !== null && (
              <Box
                mt={4}
                p={3}
                border="2px dashed #66bb6a"
                borderRadius={3}
                textAlign="center"
                bgcolor="#e8f5e9"
              >
                <Typography variant="h5" fontWeight="bold" color="success.main">
                  ₹{new Intl.NumberFormat("en-IN").format(calculatedPrice)} /{" "}
                  {getFrequencyLabel()}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Includes GST & Discounts
                </Typography>
              </Box>
            )}
          </Stack>
        </Grid>
      </Grid>
    </Paper>
  );
};

export default PricingCalculator;
